/**
 * Activity History Modal Component
 * 
 * Displays user's completed activities in a chronological timeline
 * with filtering and pagination capabilities
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface ActivityHistoryItem {
  id: string;
  timestamp: string;
  event_type: string;
  activity_name?: string;
  activity_domain?: string;
  status?: string;
  feedback_text?: string;
  duration?: string;
  description?: string;
  details?: Record<string, any>;
}

@customElement('activity-history-modal')
export class ActivityHistoryModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Array }) historyData: ActivityHistoryItem[] = [];
  @property({ type: Boolean }) loading = false;
  @state() private filterDomain = '';
  @state() private filterDateRange = '';
  @state() private expandedItems = new Set<string>();
  @state() private loadingError = false;
  private loadingTimeout: ReturnType<typeof setTimeout> | null = null;

  static styles = css`
    /* Modal Overlay */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      z-index: 2000; /* Tier 1: Standard modals (mutually exclusive with other modals) */
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
      padding: var(--spacing-4);
    }

    .modal-overlay.open {
      opacity: 1;
      pointer-events: all;
    }

    /* Modal Container */
    .modal {
      background: var(--surface-color, #ffffff);
      border-radius: var(--border-radius-lg, 12px);
      box-shadow: var(--shadow-xl, 0 25px 50px -12px rgba(0, 0, 0, 0.25));
      max-width: 800px;
      width: 100%;
      max-height: 90vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      transform: scale(0.95);
      transition: transform 0.3s ease;
    }

    .modal-overlay.open .modal {
      transform: scale(1);
    }

    /* Modal Header */
    .modal-header {
      padding: var(--spacing-6, 24px);
      border-bottom: 1px solid var(--border-color, #e5e7eb);
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: var(--surface-color, #ffffff);
    }

    .modal-title {
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--text-primary, #111827);
      margin: 0;
      display: flex;
      align-items: center;
      gap: var(--spacing-2, 8px);
    }

    .modal-close {
      background: none;
      border: none;
      font-size: 1.5rem;
      cursor: pointer;
      color: var(--text-secondary, #6b7280);
      padding: var(--spacing-2, 8px);
      border-radius: var(--border-radius, 6px);
      transition: all 0.2s ease;
    }

    .modal-close:hover {
      background: var(--surface-hover, #f3f4f6);
      color: var(--text-primary, #111827);
    }

    /* Filters */
    .filters {
      padding: var(--spacing-4, 16px) var(--spacing-6, 24px);
      border-bottom: 1px solid var(--border-color, #e5e7eb);
      display: flex;
      gap: var(--spacing-4, 16px);
      align-items: center;
      background: var(--surface-secondary, #f9fafb);
    }

    .filter-group {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-1, 4px);
    }

    .filter-group label {
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--text-secondary, #6b7280);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .filter-group select {
      padding: var(--spacing-2, 8px);
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: var(--border-radius, 6px);
      font-size: 0.875rem;
      background: var(--surface-color, #ffffff);
      color: var(--text-primary, #111827);
    }

    /* Modal Body */
    .modal-body {
      flex: 1;
      overflow-y: auto;
      padding: 0;
    }

    /* Loading State */
    .loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-12, 48px);
      color: var(--text-secondary, #6b7280);
    }

    .spinner {
      width: 32px;
      height: 32px;
      border: 3px solid var(--border-color, #e5e7eb);
      border-top: 3px solid var(--primary-color, #3b82f6);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: var(--spacing-4, 16px);
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    /* Loading Error State */
    .loading-error {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-12, 48px);
      color: var(--color-error-dark, #991b1b);
      text-align: center;
    }

    .loading-error-icon {
      font-size: 3rem;
      margin-bottom: var(--spacing-4, 16px);
      opacity: 0.7;
    }

    .loading-error h3 {
      margin: 0 0 var(--spacing-2, 8px) 0;
      color: var(--color-error-dark, #991b1b);
    }

    .loading-error p {
      color: var(--text-secondary, #6b7280);
      margin: 0;
    }

    /* Empty State */
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-12, 48px);
      color: var(--text-secondary, #6b7280);
      text-align: center;
    }

    .empty-state-icon {
      font-size: 3rem;
      margin-bottom: var(--spacing-4, 16px);
      opacity: 0.5;
    }

    /* Timeline */
    .timeline {
      padding: var(--spacing-6, 24px);
    }

    .timeline-item {
      display: flex;
      gap: var(--spacing-4, 16px);
      margin-bottom: var(--spacing-6, 24px);
      position: relative;
    }

    .timeline-item:last-child {
      margin-bottom: 0;
    }

    .timeline-item::before {
      content: '';
      position: absolute;
      left: 20px;
      top: 40px;
      bottom: -24px;
      width: 2px;
      background: var(--border-color, #e5e7eb);
    }

    .timeline-item:last-child::before {
      display: none;
    }

    .timeline-icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: var(--primary-color, #3b82f6);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.2rem;
      flex-shrink: 0;
      position: relative;
      z-index: 1;
    }

    .timeline-content {
      flex: 1;
      background: var(--surface-color, #ffffff);
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: var(--border-radius, 6px);
      padding: var(--spacing-4, 16px);
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .timeline-content:hover {
      border-color: var(--primary-color, #3b82f6);
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
    }

    .timeline-content.expanded {
      border-color: var(--primary-color, #3b82f6);
      background: var(--primary-color-light, #eff6ff);
    }

    .timeline-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: var(--spacing-2, 8px);
    }

    .timeline-title {
      font-weight: 600;
      color: var(--text-primary, #111827);
      margin: 0;
    }

    .timeline-timestamp {
      font-size: 0.75rem;
      color: var(--text-secondary, #6b7280);
      white-space: nowrap;
    }

    .timeline-meta {
      display: flex;
      gap: var(--spacing-3, 12px);
      margin-bottom: var(--spacing-2, 8px);
      font-size: 0.875rem;
    }

    .timeline-domain {
      background: var(--primary-color, #3b82f6);
      color: white;
      padding: var(--spacing-1, 4px) var(--spacing-2, 8px);
      border-radius: var(--border-radius, 6px);
      font-size: 0.75rem;
      font-weight: 500;
    }

    .timeline-duration {
      color: var(--text-secondary, #6b7280);
      font-size: 0.75rem;
    }

    .timeline-description {
      color: var(--text-primary, #111827);
      line-height: 1.5;
      font-size: 0.875rem;
    }

    .timeline-feedback {
      margin-top: var(--spacing-3, 12px);
      padding: var(--spacing-3, 12px);
      background: var(--surface-secondary, #f8fafc);
      border-radius: var(--border-radius, 6px);
      border-left: 3px solid var(--primary-color, #3b82f6);
    }

    .timeline-feedback.collapsed {
      display: none;
    }

    .timeline-feedback-label {
      font-weight: 600;
      color: var(--text-primary, #1f2937);
      margin-bottom: var(--spacing-2, 8px);
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .timeline-feedback-text {
      color: var(--text-secondary, #6b7280);
      line-height: 1.6;
      white-space: pre-wrap;
    }

    .expand-indicator {
      margin-left: auto;
      color: var(--primary-color, #3b82f6);
      font-size: 0.8rem;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: var(--spacing-1, 4px);
    }

    .expand-icon {
      transition: transform 0.2s ease;
    }

    .expand-icon.expanded {
      transform: rotate(180deg);
    }

    /* Modal Footer */
    .modal-footer {
      padding: var(--spacing-6, 24px);
      border-top: 1px solid var(--border-color, #e5e7eb);
      display: flex;
      justify-content: flex-end;
      background: var(--surface-color, #ffffff);
    }

    .btn {
      padding: var(--spacing-2, 8px) var(--spacing-4, 16px);
      border-radius: var(--border-radius, 6px);
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 1px solid transparent;
    }

    .btn-secondary {
      background: var(--surface-secondary, #f9fafb);
      color: var(--text-secondary, #6b7280);
      border-color: var(--border-color, #e5e7eb);
    }

    .btn-secondary:hover {
      background: var(--surface-hover, #f3f4f6);
      color: var(--text-primary, #111827);
    }

    /* Mobile Responsive */
    @media (max-width: 768px) {
      .modal-overlay {
        padding: var(--spacing-2, 8px);
      }

      .modal {
        max-height: 95vh;
      }

      .filters {
        flex-direction: column;
        align-items: stretch;
      }

      .timeline-header {
        flex-direction: column;
        gap: var(--spacing-1, 4px);
      }

      .timeline-meta {
        flex-wrap: wrap;
      }
    }
  `;

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('loading')) {
      if (this.loading) {
        this.loadingError = false;
        this.loadingTimeout = setTimeout(() => {
          this.loadingError = true;
          this.requestUpdate();
        }, 15000);
      } else {
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }

  /**
   * Close modal
   */
  private closeModal = () => {
    this.dispatchEvent(new CustomEvent('close-modal', { bubbles: true }));
  };

  /**
   * Toggle expanded state for an activity item
   */
  private toggleExpanded(itemId: string, event: Event) {
    event.stopPropagation();
    const newExpandedItems = new Set(this.expandedItems);
    if (newExpandedItems.has(itemId)) {
      newExpandedItems.delete(itemId);
    } else {
      newExpandedItems.add(itemId);
    }
    this.expandedItems = newExpandedItems;
  }

  /**
   * Get filtered history data
   */
  private getFilteredHistory() {
    let filtered = this.historyData;

    // Filter by domain
    if (this.filterDomain) {
      filtered = filtered.filter(item => 
        item.activity_domain === this.filterDomain
      );
    }

    // Sort by timestamp (newest first)
    return filtered.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }

  /**
   * Get unique domains for filter dropdown
   */
  private getUniqueDomains() {
    const domains = new Set<string>();
    this.historyData.forEach(item => {
      if (item.activity_domain) {
        domains.add(item.activity_domain);
      }
    });
    return Array.from(domains).sort();
  }

  /**
   * Format timestamp for display
   */
  private formatTimestamp(timestamp: string) {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  /**
   * Get icon for activity type
   */
  private getActivityIcon(eventType: string) {
    switch (eventType) {
      case 'wheel_generated':
        return '🎯';
      case 'activity_added_to_wheel':
        return '➕';
      case 'activity_removed_from_wheel':
        return '➖';
      case 'custom_activity_created':
        return '✨';
      case 'wheel_spin_initiated':
        return '🎲';
      default:
        return '📝';
    }
  }

  render() {
    if (!this.open) return html``;

    const filteredHistory = this.getFilteredHistory();
    const uniqueDomains = this.getUniqueDomains();

    return html`
      <div class="modal-overlay ${this.open ? 'open' : ''}" data-testid="history-modal-overlay" @click=${this.closeModal}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3 class="modal-title">
              📜 Activity History
            </h3>
            <button class="modal-close" data-testid="history-modal-close-btn" @click=${this.closeModal}>×</button>
          </div>

          <!-- Filters -->
          <div class="filters">
            <div class="filter-group">
              <label>Domain</label>
              <select data-testid="history-filter-domain" @change=${(e: Event) => this.filterDomain = (e.target as HTMLSelectElement).value}>
                <option value="">All Domains</option>
                ${uniqueDomains.map(domain => html`
                  <option value=${domain}>${domain}</option>
                `)}
              </select>
            </div>
          </div>

          <div class="modal-body">
            ${this.loadingError ? html`
              <div class="loading-error" data-testid="history-loading-error">
                <div class="loading-error-icon">⚠️</div>
                <h3>Unable to Load History</h3>
                <p>The request timed out. Please close this modal and try again.</p>
              </div>
            ` : this.loading ? html`
              <div class="loading-state" data-testid="history-loading-state">
                <div class="spinner"></div>
                <p>Loading activity history...</p>
              </div>
            ` : filteredHistory.length > 0 ? html`
              <div class="timeline" data-testid="history-timeline">
                ${filteredHistory.map((item, idx) => {
                  const isExpanded = this.expandedItems.has(item.id);
                  const hasFeedback = item.feedback_text && item.feedback_text.trim().length > 0;

                  return html`
                    <div class="timeline-item" data-testid="history-timeline-item-${idx}">
                      <div class="timeline-icon">
                        ${this.getActivityIcon(item.event_type)}
                      </div>
                      <div class="timeline-content ${isExpanded ? 'expanded' : ''}"
                           @click=${(e: Event) => hasFeedback ? this.toggleExpanded(item.id, e) : null}>
                        <div class="timeline-header">
                          <h4 class="timeline-title">
                            ${item.activity_name || item.event_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </h4>
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <span class="timeline-timestamp">
                              ${this.formatTimestamp(item.timestamp)}
                            </span>
                            ${hasFeedback ? html`
                              <div class="expand-indicator">
                                <span>${isExpanded ? 'Less' : 'More'}</span>
                                <span class="expand-icon ${isExpanded ? 'expanded' : ''}">▼</span>
                              </div>
                            ` : ''}
                          </div>
                        </div>
                        <div class="timeline-meta">
                          ${item.activity_domain ? html`
                            <span class="timeline-domain">${item.activity_domain}</span>
                          ` : ''}
                          ${item.duration ? html`
                            <span class="timeline-duration">Duration: ${item.duration}</span>
                          ` : ''}
                        </div>
                        ${item.description ? html`
                          <div class="timeline-description">${item.description}</div>
                        ` : ''}
                        ${hasFeedback ? html`
                          <div class="timeline-feedback ${isExpanded ? '' : 'collapsed'}">
                            <div class="timeline-feedback-label">
                              ${item.status === 'completed' ? 'Completion Feedback' :
                                item.status === 'refused' ? 'Refusal Reason' : 'Feedback'}
                            </div>
                            <div class="timeline-feedback-text">${item.feedback_text}</div>
                          </div>
                        ` : ''}
                      </div>
                    </div>
                  `;
                })}
              </div>
            ` : html`
              <div class="empty-state" data-testid="history-empty-state">
                <div class="empty-state-icon">📝</div>
                <h3>No Activity History</h3>
                <p>You haven't completed any activities yet. Start spinning the wheel to build your activity history!</p>
              </div>
            `}
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click=${this.closeModal}>
              Close
            </button>
          </div>
        </div>
      </div>
    `;
  }
}
